import messages from 'lang/messages';
import { createContext, useContext, useEffect, useReducer, useCallback } from 'react';
import { IntlProvider } from 'react-intl';
import { getEvent } from 'services/event';
import { getGuestList, setGuestVariantCookie } from 'services/guest';
import { getCharacterCloud } from 'services/characterCloud';
import { addMobileResizeListener, addShortMobileResizeListener } from 'services/responsive';
import { authenticate } from 'services/viewlogyAuth';
import { USE_MOCK_DATA } from 'features/Viewlogy/CharacterCloud/config';

const Context = createContext();

const GUEST_LIST_MIN_SIZE = 4;

const initialState = {
    urlParams: undefined,
    isLoading: true,
    event: {
        isLoggedIn: false,
        isLoginError: false,
    },
    isPinLoggedIn: false, // set to true if at least one PIN is logged in
    guestList: [],
    guestListSize: 0,
    displaySize: GUEST_LIST_MIN_SIZE,
    characterCloudData: {
        words: [],
        imageUrl: null,
        updatedAt: null,
        renderedAt: null,
    },
    streams: [],
    streamIndex: 0,
    messages: {},
    language: navigator.language.split(/[-_]/)[0] || 'en',
    isMobile: window.innerWidth < 750,
    isShortMobile: window.innerHeight < 350,
};

const reducer = (state, action) => {
    // update state through reducer actions
    switch (action.type) {
        case 'UPDATE_URL_PARAMS':
            return {
                ...state,
                urlParams: action.payload,
            };
        case 'UPDATE_IS_LOADING':
            return {
                ...state,
                isLoading: action.payload,
            };
        case 'UPDATE_IS_LOGGED_IN':
            return {
                ...state,
                event: {
                    ...state.event,
                    isLoggedIn: action.payload,
                },
            };
        case 'UPDATE_EVENT':
            const { streams, ...event } = action.payload;
            const existingStreamsById = Object.fromEntries(state.streams.map(s => [s.id, s]));

            return {
                ...state,
                event: {
                    ...state.event,
                    ...event,
                },
                streams: streams.map(newStream => ({
                    ...newStream,
                    isLoggedIn: existingStreamsById[newStream.id]?.isLoggedIn,
                })),
            };
        case 'UPDATE_STREAMS':
            return {
                ...state,
                streams: action.payload,
            };
        case 'UPDATE_STREAM_INDEX':
            return {
                ...state,
                streamIndex: action.payload,
            };
        case 'UPDATE_MESSAGES':
            return {
                ...state,
                messages: {
                    ...state.messages,
                    ...action.payload,
                },
            };
        case 'UPDATE_IS_PIN_LOGGED_IN':
            return {
                ...state,
                isPinLoggedIn: action.payload,
            };
        case 'UPDATE_GUEST_LIST':
            return {
                ...state,
                guestList: action.payload,
            };
        case 'UPDATE_GUEST_LIST_SIZE':
            return {
                ...state,
                guestListSize: action.payload,
            };
        case 'UPDATE_DISPLAY_SIZE':
            return {
                ...state,
                displaySize: action.payload,
            };
        case 'UPDATE_CHARACTER_CLOUD':
            return {
                ...state,
                characterCloudData: action.payload,
            };
        case 'UPDATE_IS_MOBILE':
            return {
                ...state,
                isMobile: action.payload,
            };
        case 'UPDATE_IS_SHORT_MOBILE':
            return {
                ...state,
                isShortMobile: action.payload,
            };
        default:
            throw new Error();
    }
};

/**
 * Provider for EventsContext state
 */
const ViewlogyProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const setUrlParams = params => dispatch({ type: 'UPDATE_URL_PARAMS', payload: params });
    const setIsLoading = isLoading => dispatch({ type: 'UPDATE_IS_LOADING', payload: isLoading });
    const setIsLoggedIn = isLoggedIn => dispatch({ type: 'UPDATE_IS_LOGGED_IN', payload: isLoggedIn });
    const setEvent = event => dispatch({ type: 'UPDATE_EVENT', payload: event });
    const setStreams = stream => dispatch({ type: 'UPDATE_STREAMS', payload: stream });
    const setStreamIndex = index => dispatch({ type: 'UPDATE_STREAM_INDEX', payload: index });
    const setMessages = messages => dispatch({ type: 'UPDATE_MESSAGES', payload: messages });
    const setIsPinLoggedIn = isPinLoggedIn => dispatch({ type: 'UPDATE_IS_PIN_LOGGED_IN', payload: isPinLoggedIn });
    const setGuestList = guestList => dispatch({ type: 'UPDATE_GUEST_LIST', payload: guestList });
    const setGuestListSize = size => dispatch({ type: 'UPDATE_GUEST_LIST_SIZE', payload: size });
    const setDisplaySize = size => dispatch({ type: 'UPDATE_DISPLAY_SIZE', payload: size });
    const setCharacterCloudData = data => dispatch({ type: 'UPDATE_CHARACTER_CLOUD', payload: data });
    const setIsMobile = isMobile => dispatch({ type: 'UPDATE_IS_MOBILE', payload: isMobile });
    const setIsShortMobile = isShortMobile => dispatch({ type: 'UPDATE_IS_SHORT_MOBILE', payload: isShortMobile });

    useEffect(() => {
        setGuestVariantCookie();
    }, []);

    useEffect(() => {
        return addMobileResizeListener(setIsMobile);
    }, []);

    useEffect(() => {
        return addShortMobileResizeListener(setIsShortMobile);
    }, []);

    return (
        <Context.Provider
            value={{
                ...state,
                setUrlParams,
                setIsLoggedIn,
                setIsLoading,
                setEvent,
                setStreams,
                setStreamIndex,
                setMessages,
                setIsPinLoggedIn,
                setGuestList,
                setGuestListSize,
                setDisplaySize,
                setCharacterCloudData,
                setIsMobile,
                setIsShortMobile,
            }}
        >
            <IntlProvider locale={state.language} messages={messages[state.language]}>
                {children}
            </IntlProvider>
        </Context.Provider>
    );
};

/**
 * Call this method to access Context state in components that use Provider
 */
const useViewlogies = () => {
    const value = useContext(Context);
    const {
        event,
        streams,
        streamIndex,
        setIsLoading,
        setIsLoggedIn,
        setEvent,
        setStreams,
        setMessages,
        setIsPinLoggedIn,
        setGuestList,
        setGuestListSize,
        setCharacterCloudData,
    } = value;

    // fetch event data for current event (based on URL params)
    const fetchEvent = async params => {
        try {
            const { status, data } = await getEvent(params);
            // getEvent handles request errors internally and resolves with { status, message } and no data
            if (!data) {
                throw new Error(`Event not found (status ${status})`);
            }
            // Check hideFromPortal field - event is still returned by API, but should not be rendered
            // Only treat as hidden if explicitly true (undefined/false means not hidden)
            if (data.hideFromPortal === true) {
                setEvent(data);
                setIsLoading(false);
                return;
            }
            setEvent(data);
            setIsLoading(false);
        } catch (e) {
            console.log('Invalid URL!');
            console.error(e);
            // redirect to 404 page
            window.location.href = 'https://www.viewlogies.com/404';
        }
    };

    const refreshEvent = async () => {
        try {
            const { organizationId, id, chapel, startTime, lastName } = event;
            const { data } = await getEvent({ organizationId, eventId: id, chapel, startTime, lastName });
            // Check hideFromPortal field
            if (data.hideFromPortal === true) {
                // Still set event data so it's available for checks, but mark it as hidden
                setEvent(data);
                return;
            }
            setEvent(data);
        } catch (e) {
            console.error(e);
        }
    };

    // log in for a video after a user provides input
    const videoLogin = async input => {
        const { globalPin, firstName, id } = event;
        const stream = streams[streamIndex];
        const authParams = globalPin
            ? {
                  pin: globalPin,
                  eventId: id,
                  firstName,
              }
            : {
                  pin: stream.pin,
                  eventId: stream.id,
                  firstName,
              };
        const _isLoggedIn = await authenticate(authParams, input);
        if (_isLoggedIn) {
            setIsPinLoggedIn(true);
            if (globalPin) {
                setIsLoggedIn(true);
            } else {
                streams[streamIndex].isLoggedIn = true;
                setStreams(streams);
            }
        } else {
            setMessages({ login: { error: true } });
        }
    };

    // fetch guest list for current event
    const fetchGuestList = async (_displaySize = value.displaySize) => {
        try {
            const { id, organizationId } = event;
            const {
                data: { guestList, guestListSize },
            } = await getGuestList(organizationId, id, 0, _displaySize);
            setGuestList(guestList);
            setGuestListSize(guestListSize); //Total guests in database, not total guests currently displayed
        } catch (e) {
            console.error(e);
        }
    };

    // fetch character cloud data for current event
    const fetchCharacterCloud = useCallback(async () => {
        // Skip API call if using mock data
        if (USE_MOCK_DATA) {
            return null;
        }
        const { id, organizationId } = event;
        if (!id || !organizationId) {
            return null;
        }
        try {
            const { data } = await getCharacterCloud(organizationId, id);
            const renderedAt = data?.renderedAt || null;
            if (data && data.words && Array.isArray(data.words)) {
                // Validate that words array has valid entries
                const validWords = data.words.filter(word => {
                    // Basic validation - word should be an object with text and value
                    return (
                        word &&
                        typeof word === 'object' &&
                        (word.text || word.word || word.adjective) &&
                        (word.value || word.count || word.frequency)
                    );
                });

                if (validWords.length > 0) {
                    setCharacterCloudData({
                        words: [...validWords],
                        imageUrl: data.imageUrl || null,
                        updatedAt: data.updatedAt || null,
                        renderedAt,
                    });

                    // Log for debugging (only in development)
                    if (process.env.NODE_ENV === 'development') {
                        console.log(`[fetchCharacterCloud] Fetched ${validWords.length} valid words from API`);
                    }
                } else {
                    console.warn('fetchCharacterCloud - No valid words in response:', data);
                    setCharacterCloudData({ words: [], imageUrl: null, updatedAt: null, renderedAt });
                }
            } else {
                console.warn('fetchCharacterCloud - No words array in response:', data);
                setCharacterCloudData({ words: [], imageUrl: null, updatedAt: null, renderedAt });
            }
            // Returned so callers can poll until the rendered image actually changes.
            return renderedAt;
        } catch (e) {
            console.error('fetchCharacterCloud - Error:', e);
            setCharacterCloudData({ words: [], imageUrl: null, updatedAt: null, renderedAt: null });
            return null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event.id, event.organizationId]);

    /**
     * Poll the character cloud after a submission until the rendered image advances past
     * `previousRenderedAt`, so the submitter sees their words appear almost immediately.
     * Regeneration runs asynchronously (debounced) on the server, so we re-check a few
     * times and stop as soon as the new image is ready (or after a short timeout).
     */
    const refreshCharacterCloudUntilRendered = useCallback(
        async (previousRenderedAt, { attempts = 12, intervalMs = 1200 } = {}) => {
            if (USE_MOCK_DATA) {
                return false;
            }
            for (let attempt = 0; attempt < attempts; attempt++) {
                // eslint-disable-next-line no-await-in-loop
                await new Promise(resolve => setTimeout(resolve, intervalMs));
                // eslint-disable-next-line no-await-in-loop
                const renderedAt = await fetchCharacterCloud();
                if (renderedAt && renderedAt !== previousRenderedAt) {
                    return true;
                }
            }
            return false;
        },
        [fetchCharacterCloud],
    );

    const fetchPausedStatus = async () => {
        try {
            const { organizationId, id, chapel, startTime, lastName } = event;
            const { data } = await getEvent({ organizationId, eventId: id, chapel, startTime, lastName });
            // Check hideFromPortal field
            if (data.hideFromPortal === true) {
                // Still set event data so it's available for checks, but mark it as hidden
                setEvent(data);
                return;
            }
            const updatedPausedStatusData = {
                ...data,
                streams: data.streams.map((stream, index) => {
                    return {
                        ...data.streams[index],
                        paused: stream.paused,
                    };
                }),
            };
            setEvent(updatedPausedStatusData);
        } catch (e) {
            console.error(e);
        }
    };

    return {
        fetchEvent,
        refreshEvent,
        videoLogin,
        fetchGuestList,
        fetchCharacterCloud,
        refreshCharacterCloudUntilRendered,
        fetchPausedStatus,
        ...value,
    };
};

export { useViewlogies, ViewlogyProvider };
